import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, MapPin, Calendar, UserPlus, UserCheck, Package } from 'lucide-react';
import ListingCard from '../components/listing/ListingCard';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { formatDate, getCustomerBadges, getRoleBadgeInfo } from '../utils/formatters';
import { listingService } from '../services/listingService';
import { followService } from '../services/followService';

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const { t } = useLanguage();
  const [profileUser, setProfileUser] = useState(null);
  const [userListings, setUserListings] = useState([]);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('listings');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const listings = await listingService.getBySeller(id);
        setUserListings(listings || []);
        if (listings?.[0]?.seller) setProfileUser(listings[0].seller);
      } catch {}
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (currentUser && id && currentUser.id !== id) {
      followService.isFollowing(id).then(setFollowing).catch(() => {});
    }
  }, [id, currentUser]);

  const handleFollow = async () => {
    if (!currentUser) { navigate('/login'); return; }
    try {
      if (following) {
        await followService.unfollow(id);
        setFollowing(false);
      } else {
        await followService.follow(id);
        setFollowing(true);
      }
    } catch {}
  };

  if (loading) return (
    <div className="max-w-5xl mx-auto px-4 py-20 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-apple-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profileUser && userListings.length === 0) return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <p className="text-xl font-semibold text-apple-gray-dark mb-4">{t('not_found.subtitle')}</p>
      <Link to="/" className="btn-primary">{t('not_found.home')}</Link>
    </div>
  );

  const name = profileUser?.userNameSurname || profileUser?.name || 'Kullanıcı';
  const roleBadge = getRoleBadgeInfo(profileUser?.role);
  const badges = getCustomerBadges(profileUser?.badges || []);
  const isOwnProfile = currentUser?.id === id || currentUser?.id === profileUser?.id;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="card p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-apple-blue to-blue-400 flex items-center justify-center text-white text-3xl font-bold shrink-0">
            {name[0]}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-2xl font-bold text-apple-gray-dark">{name}</h1>
              {profileUser?.isVerified && <span className="text-apple-blue text-lg" title="Onaylı Üye">✓</span>}
              <span className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${roleBadge.className}`}>{t(roleBadge.labelKey)}</span>
            </div>

            {badges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {badges.map(b => (
                  <span key={b.labelKey} className={`text-xs font-medium px-2.5 py-1 rounded-full ${b.className}`}>{b.icon} {t(b.labelKey)}</span>
                ))}
              </div>
            )}

            {profileUser?.bio && <p className="text-sm text-apple-gray-mid mb-3">{profileUser.bio}</p>}

            <div className="flex flex-wrap items-center gap-4 text-sm text-apple-gray-mid">
              {profileUser?.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profileUser.city}</span>}
              {profileUser?.createDate && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(profileUser.createDate)} {t('profile.member_since_label')}</span>}
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" /> {profileUser?.rating || 0} {t('profile.rating_label')}</span>
              <span className="flex items-center gap-1"><Package className="w-3.5 h-3.5" /> {profileUser?.totalSales || 0} {t('profile.sales_label')}</span>
            </div>
          </div>

          {!isOwnProfile && (
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleFollow}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${following ? 'bg-apple-blue text-white' : 'border border-apple-blue text-apple-blue hover:bg-apple-blue hover:text-white'}`}
              >
                {following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {following ? t('listing_detail.unfollow') : t('listing_detail.follow')}
              </button>
            </div>
          )}
          {isOwnProfile && (
            <Link to="/settings" className="btn-secondary text-sm px-4 py-2 shrink-0">Profili Düzenle</Link>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-apple-gray-dark">{(profileUser?.followerCount || 0).toLocaleString('de-DE')}</p>
            <p className="text-xs text-apple-gray-mid">{t('listing_detail.follow')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-apple-gray-dark">{(profileUser?.followingCount || 0).toLocaleString('de-DE')}</p>
            <p className="text-xs text-apple-gray-mid">{t('listing_detail.unfollow')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-apple-gray-dark">{userListings.length}</p>
            <p className="text-xs text-apple-gray-mid">{t('profile.listings')}</p>
          </div>
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-6 gap-6">
        {[['listings', t('profile.listings'), userListings.length], ['reviews', t('profile.reviews'), 0]].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === key ? 'border-apple-blue text-apple-blue' : 'border-transparent text-apple-gray-mid hover:text-apple-gray-dark'}`}
          >
            {label} <span className="ml-1 text-xs bg-apple-gray text-apple-gray-mid px-1.5 py-0.5 rounded-full">{count}</span>
          </button>
        ))}
      </div>

      {activeTab === 'listings' && (
        userListings.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-apple-gray-mid">{t('profile.no_listings')}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {userListings.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        )
      )}

      {activeTab === 'reviews' && (
        <div className="text-center py-16">
          <p className="text-apple-gray-mid">{t('profile.no_reviews')}</p>
        </div>
      )}
    </div>
  );
}
